import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

export type CtaState = 'create_book' | 'view_pricing';

const FREE_CREDIT_DESCRIPTION = 'Free starter credit — create your first complete e-book';

/**
 * Determines what CTA to show based on the user's status:
 * - Not authenticated → "See plans & pricing" → /pricing
 * - Has subscription or lifetime deal → "Create Book" → /create-book
 * - Has paid credits (PAYG) → "Create Book" → /create-book (full features unlocked)
 * - Has only free trial credit → "Create Book" → /create-book (limited features)
 * - Authenticated, no credits, no subscription, no lifetime deal → "View Pricing" → /pricing
 *
 * Also returns `hasPaidFeatures` — whether the user has paid for full access
 * (subscription, lifetime deal, or PAYG credits — NOT the free trial credit).
 */
export function useHasActivePlan() {
    const { data, isLoading } = useQuery({
        queryKey: ['hasActivePlan'],
        queryFn: async () => {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            if (!user) {
                return {
                    ctaState: 'view_pricing' as CtaState,
                    isAuthenticated: false,
                    hasPaidFeatures: false,
                    hasPastDueSubscription: false,
                };
            }

            const [subscriptionsResult, lifetimeDealResult, creditsResult, transactionsResult] =
                await Promise.all([
                    supabase
                        .from('subscriptions')
                        .select('id, status')
                        .eq('user_id', user.id)
                        .order('created_at', { ascending: false })
                        .limit(1)
                        .maybeSingle(),
                    supabase
                        .from('lifetime_deals')
                        .select('id')
                        .eq('user_id', user.id)
                        .eq('status', 'active')
                        .limit(1)
                        .maybeSingle(),
                    supabase
                        .from('credits')
                        .select('balance')
                        .eq('user_id', user.id)
                        .maybeSingle(),
                    supabase
                        .from('credits_transactions')
                        .select('description')
                        .eq('user_id', user.id)
                        .eq('type', 'earned')
                        .neq('description', FREE_CREDIT_DESCRIPTION)
                        .limit(1)
                        .maybeSingle(),
                ]);

            const subscriptionStatus = subscriptionsResult.data?.status;
            const hasSubscription = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
            const hasPastDueSubscription = subscriptionStatus === 'past_due';
            const hasLifetimeDeal = !!lifetimeDealResult.data;
            const hasPaidCredits = !!transactionsResult.data; // PAYG or other non-free credit purchases
            const hasCredits = (creditsResult.data?.balance ?? 0) > 0;

            // Paid features = subscription, lifetime deal, or PAYG credits
            const hasPaidFeatures = hasSubscription || hasLifetimeDeal || hasPaidCredits;

            let ctaState: CtaState;
            if (hasSubscription || hasLifetimeDeal) {
                ctaState = 'create_book';
            } else if (hasCredits) {
                ctaState = 'create_book';
            } else {
                ctaState = 'view_pricing';
            }

            return { ctaState, isAuthenticated: true, hasPaidFeatures, hasPastDueSubscription };
        },
        staleTime: 30_000, // 30 seconds
    });

    return {
        ctaState: data?.ctaState ?? 'view_pricing',
        isAuthenticated: data?.isAuthenticated ?? false,
        hasPaidFeatures: data?.hasPaidFeatures ?? false,
        hasPastDueSubscription: data?.hasPastDueSubscription ?? false,
        isLoading,
    };
}
